import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const CustomFieldsTab = () => {
  const { profile } = useAuth();
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newField, setNewField] = useState({ entity_type: 'contacts', field_name: '', field_type: 'text', required: false });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const entityOptions = [
    { value: 'contacts', label: 'Contacts' },
    { value: 'accounts', label: 'Accounts' },
    { value: 'deals', label: 'Deals' },
    { value: 'activities', label: 'Activities' }
  ];

  const fieldTypeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Boolean (Yes/No)' },
    { value: 'select', label: 'Select (Dropdown)' }
  ];

  const fetchCustomFields = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('custom_fields')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at');
      if (error) throw error;
      setCustomFields(data || []);
    } catch (err) {
      setErrorMsg(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchCustomFields(); }, [fetchCustomFields]);

  const handleCreateField = async () => {
    if (!newField?.field_name?.trim()) return;
    setIsSaving(true);
    setErrorMsg('');
    try {
      const { error } = await supabase?.from('custom_fields')?.insert({
        org_id: profile?.org_id,
        entity_type: newField?.entity_type,
        field_name: newField?.field_name,
        field_type: newField?.field_type,
        required: newField?.required
      });
      if (error) throw error;
      setNewField({ entity_type: 'contacts', field_name: '', field_type: 'text', required: false });
      setIsAddModalOpen(false);
      setSuccessMsg('Custom field created!');
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchCustomFields();
    } catch (err) {
      setErrorMsg(err?.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this custom field?')) return;
    try {
      await supabase?.from('custom_fields')?.delete()?.eq('id', fieldId);
      fetchCustomFields();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  const groupedFields = customFields?.reduce((acc, field) => {
    if (!acc?.[field?.entity_type]) acc[field.entity_type] = [];
    acc?.[field?.entity_type]?.push(field);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {successMsg && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMsg}</div>}
      {errorMsg && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errorMsg}</div>}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Icon name="Settings" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Custom Fields</h3>
              <p className="text-sm text-muted-foreground">Create custom fields for your CRM entities</p>
            </div>
          </div>
          <Button variant="default" onClick={() => setIsAddModalOpen(true)} iconName="Plus" iconPosition="left">
            Add Field
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3]?.map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : customFields?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Settings" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No custom fields yet. Add your first field.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedFields)?.map(([entityType, fields]) => (
              <div key={entityType}>
                <h4 className="font-medium text-card-foreground capitalize mb-3">{entityType}</h4>
                <div className="space-y-2">
                  {fields?.map(field => (
                    <div key={field?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="Tag" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-card-foreground text-sm">{field?.field_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{field?.field_type}{field?.required ? ' • Required' : ''}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteField(field?.id)} className="text-error hover:text-error/80 transition-smooth">
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Add Custom Field</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <Select label="Entity Type" options={entityOptions} value={newField?.entity_type}
                onChange={(value) => setNewField(p => ({...p, entity_type: value}))} />
              <Input label="Field Name" type="text" value={newField?.field_name}
                onChange={(e) => setNewField(p => ({...p, field_name: e?.target?.value}))} required />
              <Select label="Field Type" options={fieldTypeOptions} value={newField?.field_type}
                onChange={(value) => setNewField(p => ({...p, field_type: value}))} />
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="required" checked={newField?.required}
                  onChange={(e) => setNewField(p => ({...p, required: e?.target?.checked}))}
                  className="rounded border-border" />
                <label htmlFor="required" className="text-sm text-foreground">Required field</label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleCreateField} loading={isSaving}
                disabled={isSaving || !newField?.field_name?.trim()}>
                {isSaving ? 'Creating...' : 'Create Field'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsTab;